import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import logo from '../../../assets/images/farmstream_logo.png';
import { getErrorMessage } from '../../../utils';
import { useTradeRequestLoginMutation } from '../api/tradeApi';

export const TradeLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');

  const [requestLogin, { isLoading: isRequestingLogin }] = useTradeRequestLoginMutation();

  // If the backend is in TEST_MODE it returns the magic link directly so we can
  // skip the email round-trip and log straight in.
  const followMagicLink = (magicLink?: string) => {
    const token = magicLink?.split('/').pop();
    if (token) {
      navigate('/trade/login/' + token);
      return true;
    }
    return false;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await requestLogin({ email }).unwrap();
      if (followMagicLink(res.magicLink)) return;
      toast.success(res.message || 'Login link sent, please check your email');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not send login link'));
    }
  };

  return (
    <Background>
      <StyledImg src={logo} alt="Farmstream logo" />
      <Container>
        <Title>Trade Partner Portal</Title>
        <Subtitle>
          Sign in to manage your referrals and track your commission.
        </Subtitle>

        <Form onSubmit={handleLogin}>
          <Field>
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="you@business.co.uk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </Field>
          <SubmitButton type="submit" disabled={isRequestingLogin}>
            {isRequestingLogin ? 'Sending…' : 'Send login link'}
          </SubmitButton>
        </Form>
      </Container>
      <BackLink href="https://www.farmstream.co.uk/">← Back to farmstream.co.uk</BackLink>
    </Background>
  );
};

const Background = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  background-color: ${({ theme }) => theme.colors.primary};
  padding: 1rem;
  gap: 1.5rem;
`;

const StyledImg = styled.img`
  max-width: 8rem;
`;

const Container = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  @media (max-width: 500px) {
    padding: 1.5rem;
    max-width: unset;
  }
`;

const Title = styled.h2`
  margin: 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray900};
`;

const Subtitle = styled.p`
  margin: 0;
  text-align: center;
  color: #667085;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray900};
`;

const Input = styled.input`
  padding: 0.6rem;
  border-radius: 0.4rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  font-size: 1rem;
`;

const SubmitButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.7rem 1rem;
  font-size: 1rem;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  color: #667085;
  font-size: 0.8rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray500};
  }

  span {
    padding: 0 0.75rem;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: underline;
`;

const BackLink = styled.a`
  color: ${({ theme }) => theme.colors.white};
  font-size: 0.9rem;
  text-decoration: none;
  opacity: 0.85;

  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;
